import { useReducer } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { toast } from "sonner";

import type { SafetyMeetingLiveAttendanceMeetingSnapshot } from "@miter/types/common";
import type { PublicLiveAttendanceJoinRequest } from "@miter/types/contracts";
import { Button, Loader, type ESignatureValue } from "ui2";

import { AlreadyOnListScreen } from "safety/components/attendance/AlreadyOnListScreen";
import { AttendanceLayout } from "safety/components/attendance/AttendanceLayout";
import { ClosedScreen } from "safety/components/attendance/ClosedScreen";
import { ConfirmScreen } from "safety/components/attendance/ConfirmScreen";
import { ForgotPinScreen } from "safety/components/attendance/ForgotPinScreen";
import { PIN_LENGTH } from "safety/components/attendance/format";
import { LandingScreen } from "safety/components/attendance/LandingScreen";
import { PinScreen } from "safety/components/attendance/PinScreen";
import { ReceiptScreen } from "safety/components/attendance/ReceiptScreen";
import { VisitorScreen } from "safety/components/attendance/VisitorScreen";
import { PublicSafetyStatusMessage } from "safety/components/PublicSafetyPage";
import {
  AlreadyOnListError,
  AttendanceNotFoundError,
  AttendanceUnavailableError,
  MiterAPI,
  PinNotRecognizedError,
  TooManyAttemptsError,
} from "safety/miter";

// Kept on the receipt so the confirmation can render without a live session.
type Receipt = {
  name: string;
  signed_at: number;
  meeting_snapshot: SafetyMeetingLiveAttendanceMeetingSnapshot;
};

// Each screen carries only its own data, so a transition can't leave a stale PIN, name, or
// signature behind. Outcome states (loading, not found, closed) are derived from the query, not here.
type Flow =
  | { name: "landing" }
  | { name: "pin"; pin: string; error: string | null }
  | { name: "forgotPin"; phone: string; sent: boolean }
  | { name: "confirm"; memberName: string; pin: string; signature: ESignatureValue }
  | { name: "visitor"; visitorName: string; signature: ESignatureValue }
  | { name: "already"; memberName: string }
  | { name: "done"; receipt: Receipt };

type FlowAction =
  | { type: "choosePin" }
  | { type: "chooseVisitor" }
  | { type: "pinChanged"; pin: string }
  | { type: "pinRejected"; message: string }
  | { type: "pinMatched"; memberName: string; pin: string }
  | { type: "chooseForgotPin" }
  | { type: "forgotPhoneChanged"; phone: string }
  | { type: "forgotPinSent" }
  | { type: "showAlready"; memberName: string }
  | { type: "signatureChanged"; signature: ESignatureValue }
  | { type: "visitorNameChanged"; visitorName: string }
  | { type: "joined"; receipt: Receipt }
  | { type: "reset" };

const reduceFlow = (flow: Flow, action: FlowAction): Flow => {
  switch (action.type) {
    case "choosePin":
      return { name: "pin", pin: "", error: null };
    case "chooseVisitor":
      return { name: "visitor", visitorName: "", signature: {} };
    case "pinChanged":
      return flow.name === "pin" ? { ...flow, pin: action.pin, error: null } : flow;
    case "pinRejected":
      return { name: "pin", pin: "", error: action.message };
    case "pinMatched":
      return { name: "confirm", memberName: action.memberName, pin: action.pin, signature: {} };
    case "chooseForgotPin":
      return { name: "forgotPin", phone: "", sent: false };
    case "forgotPhoneChanged":
      return flow.name === "forgotPin" ? { ...flow, phone: action.phone } : flow;
    case "forgotPinSent":
      return flow.name === "forgotPin" ? { ...flow, sent: true } : flow;
    case "showAlready":
      return { name: "already", memberName: action.memberName };
    case "signatureChanged":
      return flow.name === "confirm" || flow.name === "visitor"
        ? { ...flow, signature: action.signature }
        : flow;
    case "visitorNameChanged":
      return flow.name === "visitor" ? { ...flow, visitorName: action.visitorName } : flow;
    case "joined":
      return { name: "done", receipt: action.receipt };
    case "reset":
      return { name: "landing" };
    default:
      return flow;
  }
};

const receiptStorageKey = (code: string): string => `miter-attendance-receipt:${code}`;

const readReceipt = (code: string): Receipt | null => {
  try {
    const raw = localStorage.getItem(receiptStorageKey(code));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Receipt>;
    if (!parsed.name || !parsed.signed_at || !parsed.meeting_snapshot) return null;
    return parsed as Receipt;
  } catch {
    return null;
  }
};

const AttendancePage = (): React.JSX.Element => {
  const { code } = useParams<{ code: string }>();

  // Reloading the URL on the same phone comes back to the receipt, not the keypad.
  const [flow, dispatch] = useReducer(reduceFlow, null, (): Flow => {
    const saved = code ? readReceipt(code) : null;
    return saved ? { name: "done", receipt: saved } : { name: "landing" };
  });

  const {
    data: session,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["public-live-attendance", code],
    queryFn: () => MiterAPI.safety.liveAttendance.retrieve(code!),
    enabled: !!code,
    retry: false,
    refetchInterval: 30 * 1000,
  });

  const pinLookup = useMutation({
    mutationFn: (kioskPin: string) => MiterAPI.safety.liveAttendance.lookupPin(code!, kioskPin),
    onSuccess: (match, kioskPin) =>
      dispatch(
        match.already_signed_in
          ? { type: "showAlready", memberName: match.name }
          : { type: "pinMatched", memberName: match.name, pin: kioskPin }
      ),
    onError: (lookupError) => {
      if (lookupError instanceof PinNotRecognizedError) {
        dispatch({
          type: "pinRejected",
          message: "That PIN doesn't match anyone at this company. Check the digits and try again.",
        });
      } else if (lookupError instanceof TooManyAttemptsError) {
        dispatch({ type: "pinRejected", message: "Too many tries. Wait a minute, then try again." });
      } else if (lookupError instanceof AttendanceUnavailableError) {
        void refetch();
      } else {
        dispatch({
          type: "pinRejected",
          message: "Something went wrong. Check your connection and try again.",
        });
      }
    },
  });

  const join = useMutation({
    mutationFn: (params: PublicLiveAttendanceJoinRequest) =>
      MiterAPI.safety.liveAttendance.join(code!, params),
    onSuccess: (result) => {
      const receipt = {
        name: result.name,
        signed_at: result.signed_at,
        meeting_snapshot: result.meeting_snapshot,
      };
      localStorage.setItem(receiptStorageKey(code!), JSON.stringify(receipt));
      dispatch({ type: "joined", receipt });
    },
    onError: (joinError) => {
      if (joinError instanceof AlreadyOnListError && flow.name === "confirm") {
        dispatch({ type: "showAlready", memberName: flow.memberName });
      } else if (joinError instanceof AttendanceUnavailableError) {
        void refetch();
      } else {
        toast.error("Couldn't submit your attendance. Try again.");
      }
    },
  });

  const sendPin = useMutation({
    mutationFn: (phoneNumber: string) => MiterAPI.safety.liveAttendance.sendPin(code!, phoneNumber),
    onSuccess: () => dispatch({ type: "forgotPinSent" }),
    onError: (sendError) => {
      if (sendError instanceof TooManyAttemptsError) {
        toast.error("Too many tries. Wait a minute, then try again.");
      } else if (sendError instanceof AttendanceUnavailableError) {
        void refetch();
      } else {
        toast.error("Couldn't text your PIN. Check the number and try again.");
      }
    },
  });

  const reset = (): void => dispatch({ type: "reset" });
  const choosePin = (): void => dispatch({ type: "choosePin" });
  const changeSignature = (signature: ESignatureValue): void =>
    dispatch({ type: "signatureChanged", signature });

  const handlePinChange = (value: string): void => {
    if (pinLookup.isPending) return;
    const next = value.replace(/\D/g, "").slice(0, PIN_LENGTH);
    dispatch({ type: "pinChanged", pin: next });
    if (next.length === PIN_LENGTH) pinLookup.mutate(next);
  };

  const submit = (): void => {
    if (flow.name === "confirm" && flow.signature.data) {
      join.mutate({ is_custom: false, pin: flow.pin, signature_image: flow.signature.data });
    } else if (flow.name === "visitor" && flow.signature.data) {
      join.mutate({ is_custom: true, name: flow.visitorName.trim(), signature_image: flow.signature.data });
    }
  };

  const renderBody = (): React.JSX.Element => {
    // Ahead of the read states so the receipt survives the session closing, which 404s the read.
    if (flow.name === "done") {
      return (
        <ReceiptScreen
          snapshot={flow.receipt.meeting_snapshot}
          name={flow.receipt.name}
          signedAt={flow.receipt.signed_at}
          onAddAnother={reset}
        />
      );
    }

    if (isLoading) {
      return <Loader className="tw:min-h-[520px] tw:flex-1" size="lg" />;
    }

    // Unknown code and closed session both arrive as 404, so this one screen covers both.
    if (!code || error instanceof AttendanceNotFoundError) {
      return (
        <PublicSafetyStatusMessage
          title="Meeting sign-in not found"
          description="This link is invalid or no longer available. Ask your supervisor to add you, or to share the code again."
        />
      );
    }

    if (!session) {
      return (
        <PublicSafetyStatusMessage
          title="Unable to load this meeting"
          description="Something went wrong while loading this sign-in page. Check your connection and try again."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      );
    }

    const snapshot = session.meeting_snapshot;

    if (!session.is_accepting_joins) return <ClosedScreen snapshot={snapshot} />;

    if (flow.name === "already") {
      return <AlreadyOnListScreen memberName={flow.memberName} onAddAnother={reset} />;
    }

    if (flow.name === "pin") {
      return (
        <PinScreen
          meetingName={snapshot.meeting_name}
          pin={flow.pin}
          pinError={flow.error}
          disabled={pinLookup.isPending}
          onChange={handlePinChange}
          onBack={reset}
          onForgotPin={() => dispatch({ type: "chooseForgotPin" })}
        />
      );
    }

    if (flow.name === "forgotPin") {
      return (
        <ForgotPinScreen
          meetingName={snapshot.meeting_name}
          phone={flow.phone}
          sent={flow.sent}
          submitting={sendPin.isPending}
          onPhoneChange={(phone) => dispatch({ type: "forgotPhoneChanged", phone })}
          onSubmit={() => sendPin.mutate(flow.phone.trim())}
          onBack={choosePin}
        />
      );
    }

    if (flow.name === "confirm") {
      return (
        <ConfirmScreen
          meetingName={snapshot.meeting_name}
          memberName={flow.memberName}
          signature={flow.signature}
          submitting={join.isPending}
          onSignatureChange={changeSignature}
          onChangePin={choosePin}
          onSubmit={submit}
        />
      );
    }

    if (flow.name === "visitor") {
      return (
        <VisitorScreen
          meetingName={snapshot.meeting_name}
          visitorName={flow.visitorName}
          signature={flow.signature}
          submitting={join.isPending}
          onVisitorNameChange={(visitorName) => dispatch({ type: "visitorNameChanged", visitorName })}
          onSignatureChange={changeSignature}
          onBack={reset}
          onSubmit={submit}
        />
      );
    }

    return (
      <LandingScreen
        snapshot={snapshot}
        signInTeamMember={choosePin}
        signInVisitor={() => dispatch({ type: "chooseVisitor" })}
      />
    );
  };

  return <AttendanceLayout>{renderBody()}</AttendanceLayout>;
};

export default AttendancePage;
